import { useRef, type RefObject } from "react";
import { Navigate } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { AgencyCashCard } from "@/domains/Finance/agency/AgencyCashCard";
import { AgencyCashDrawer } from "@/domains/Finance/agency/AgencyCashDrawer";
import { ClaimCapCard, ClaimCapDrawer } from "@/domains/Finance/agency/ClaimCapCard";
import { OwnClientCard, OwnClientDrawer } from "@/domains/Finance/agency/OwnClientCard";
import { useOpenerFocus } from "@/domains/Finance/agency/useOpenerFocus";
import { useFinanceParams, type ParamPatch } from "@/domains/Finance/hooks/useFinanceParams";
import { AddCategoryDrawer } from "@/domains/Finance/expenses/AddCategoryDrawer";
import { ExpenseCategoryModal } from "@/domains/Finance/expenses/ExpenseCategoryModal";
import type { ExpenseCategory } from "@/domains/Finance/types";
import { ExpenseCategoriesPanel } from "./ExpenseCategoriesPanel";
import { GmailConnectCard } from "./GmailConnectCard";
import { ObjectiveTypesCard } from "./ObjectiveTypesCard";
import { TeamPanel } from "./TeamPanel";
import { TelegramConnectCard } from "./TelegramConnectCard";

/**
 * The client-settings grid of WorkspacePanels, led by the agency's cash and the Team panel, ending with the expense
 * categories, moved here from /finance's Business expenses as agency config (DD-047, J18, J40, finance-real round 2).
 * Beside Agency cash sit Team claims, the most the team can claim from a job (`modal=claim-cap`), and the agency's own
 * client (TD-105, `modal=own-client`), which the client page no longer sets (finance consolidation, the user 2026-10-22).
 */
function AgencyPanels({
  categoriesRef,
  onEditCash,
  onEditClaimCap,
  onEditOwnClient,
  onOpenCategory,
  onAddCategory,
}: {
  /** The categories panel: where focus falls back to once its own drawer deleted the category that opened it. */
  categoriesRef: RefObject<HTMLDivElement>;
  onEditCash: () => void;
  onEditClaimCap: () => void;
  onEditOwnClient: () => void;
  onOpenCategory: (category: ExpenseCategory) => void;
  onAddCategory: () => void;
}) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="px-4 lg:px-6 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <div className="h-full">
            <AgencyCashCard onEdit={onEditCash} />
          </div>
          <div className="h-full">
            <ClaimCapCard onEdit={onEditClaimCap} />
          </div>
          <div className="h-full">
            <OwnClientCard onEdit={onEditOwnClient} />
          </div>
          <div className="h-full">
            <TeamPanel />
          </div>
          <div className="h-full">
            <GmailConnectCard />
          </div>
          <div className="h-full">
            <TelegramConnectCard />
          </div>
          <div className="h-full">
            <ObjectiveTypesCard />
          </div>
          <div ref={categoriesRef} className="h-full">
            <ExpenseCategoriesPanel onOpen={onOpenCategory} onAdd={onAddCategory} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Agency-wide settings (DD-047): the panel grid, led by the agency's cash on a date, which /finance's Cash counts from
 * (J18). It edits in a drawer named in the URL (`modal=agency-cash`), as a category's is (`modal=expense-category`,
 * `modal=add-category`). Finance is open to every admin (DD-040), so the cards and their drawers are too (J40). The price
 * list went back to /finance as its own tab (the user 2026-10-30), and its old address here goes there. The admin tabs
 * already name the page, so it has no title. Each cell stretches to its row; a collapsed panel drops `h-full` and stays
 * header-height inside it.
 */
export function SetupView() {
  const { params, set, closeModals } = useFinanceParams();
  const category = params.get("category");
  const closeCategory = () => closeModals({ modal: null, category: null });
  // Closing a drawer gives focus back to the panel action or button that opened it (the S7 review), as the client page's
  // drawers do: the drawers open from the URL, so Radix has no trigger to return to.
  const modal = params.get("modal");
  // A category deleted in its own drawer took its row with it: focus falls back to the panel's Add category.
  const categoriesRef = useRef<HTMLDivElement>(null);
  const markOpener = useOpenerFocus(modal, () => [
    Array.from(categoriesRef.current?.querySelectorAll<HTMLButtonElement>("button") ?? []).find((b) => b.textContent === ENTITY_CONFIG.expenseCategory.createLabel),
  ]);
  const open = (patch: ParamPatch) => {
    if (modal === null) markOpener();
    set(patch);
  };
  // The price list's old address: /finance's tab, with whatever drawer the link named, in place of it in the history.
  if (params.get("tab") === "prices") return <Navigate to={`/finance?${params.toString()}`} replace />;
  return (
    <PageLayout>
      <AgencyPanels
        categoriesRef={categoriesRef}
        onEditCash={() => open({ modal: "agency-cash" })}
        onEditClaimCap={() => open({ modal: "claim-cap" })}
        onEditOwnClient={() => open({ modal: "own-client" })}
        onOpenCategory={(c) => open({ modal: "expense-category", category: c.id })}
        onAddCategory={() => open({ modal: "add-category", category: null })}
      />
      {modal === "agency-cash" && <AgencyCashDrawer onClose={() => closeModals({ modal: null })} />}
      {modal === "claim-cap" && <ClaimCapDrawer onClose={() => closeModals({ modal: null })} />}
      {modal === "own-client" && <OwnClientDrawer onClose={() => closeModals({ modal: null })} />}
      {modal === "add-category" && <AddCategoryDrawer onClose={closeCategory} />}
      {/* Opened from the panel, with no list behind it: no Back (J26, R15). */}
      {modal === "expense-category" && category && <ExpenseCategoryModal key={category} categoryId={category} onClose={closeCategory} />}
    </PageLayout>
  );
}
